import { flexRender, type Row } from "@tanstack/react-table";
import { TableCell, TableRow } from "@volca/ui/components/ui/table";
import { cn } from "@volca/ui/lib/utils";
import {
  alignClasses,
  cellClasses,
  getPinnedCellClassName,
  getPinnedEdgeClassName,
  getPinningStyles,
  ROW_CLASSES,
  ROW_VARIANT_ROW_CLASSES,
} from "./styles";
import type { SimpleTableRowVariant } from "./types";

interface SimpleTableBodyRowProps<TData> {
  row: Row<TData>;
  isStriped: boolean;
  isLastRow: boolean;
  isSelected: boolean;
  rowVariant?: SimpleTableRowVariant;
  rowDataCy?: string;
  onRowClick?: (row: TData, tableRow?: Row<TData>) => void;
  onRowMouseEnter?: (row: TData) => void;
  onRowMouseLeave?: (row: TData) => void;
}

export const SimpleTableBodyRow = <TData,>({
  row,
  isStriped,
  isLastRow,
  isSelected,
  rowVariant,
  rowDataCy,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
}: SimpleTableBodyRowProps<TData>) => (
  <TableRow
    onClick={() => onRowClick?.(row.original, row)}
    onKeyDown={
      onRowClick &&
      ((event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onRowClick(row.original, row);
      })
    }
    tabIndex={onRowClick ? 0 : undefined}
    onMouseEnter={() => onRowMouseEnter?.(row.original)}
    onMouseLeave={() => onRowMouseLeave?.(row.original)}
    data-cy={rowDataCy}
    data-state={isSelected ? "selected" : "not-selected"}
    className={cn(
      ROW_CLASSES,
      "group h-18 w-full",
      {
        "relative cursor-pointer outline-none hover:bg-neutral-hovered/50 focus-visible:bg-neutral/50 focus-visible:after:pointer-events-none focus-visible:after:absolute focus-visible:after:inset-0 focus-visible:after:z-3 focus-visible:after:outline-3 focus-visible:after:-outline-offset-3 focus-visible:after:outline-focus/50":
          !!onRowClick,
        "bg-stripe": isStriped,
      },
      rowVariant && ROW_VARIANT_ROW_CLASSES[rowVariant],
    )}
  >
    {row.getVisibleCells().map((cell) => (
      <TableCell
        key={cell.id}
        style={getPinningStyles(cell.column)}
        className={cn(
          cellClasses(isLastRow),
          alignClasses(cell.column),
          getPinnedEdgeClassName(cell.column),
          getPinnedCellClassName(
            cell.column,
            isStriped,
            isLastRow,
            rowVariant ?? "default",
          ),
        )}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    ))}
  </TableRow>
);
